/**
 * Home/Dashboard route
 * Main landing page after authentication
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useLogout, authKeys } from '@/hooks/useAuth';
import { updateUser } from '@/lib/api/users';
import { requireAuth } from '@/lib/auth/routeGuards';
import { getMe } from '@/lib/api/auth';

export const Route = createFileRoute('/')({
  beforeLoad: requireAuth,
  component: HomeComponent,
});

function HomeComponent() {
  const { t } = useTranslation();
  // Get initial user from route context - guaranteed non-null by requireAuth
  const { user: initialUser } = Route.useRouteContext();

  // Use React Query to get latest user data (reactive to cache updates)
  const { data: authData } = useQuery({
    queryKey: authKeys.user(),
    queryFn: () => getMe(),
    initialData: { valid: true, user: initialUser },
    staleTime: Infinity, // Only refetch when explicitly invalidated
  });

  const user = authData.user;

  if (!user) {
    return null;
  }

  // Use logout hook without triggering auth verification query
  const logout = useLogout();
  // Track UI state for the switch
  const [isUpdatingAvailability, setIsUpdatingAvailability] = useState(false);
  const queryClient = useQueryClient();

  // Update user default availability mutation
  const updateAvailabilityMutation = useMutation({
    mutationFn: (defaultAvailability: boolean) =>
      updateUser(user.id, { defaultAvailability }),
    onSuccess: (updatedUser) => {
      // Update auth cache
      queryClient.setQueryData(authKeys.user(), {
        valid: true,
        user: updatedUser,
      });
      // Invalidate to refetch
      queryClient.invalidateQueries({
        queryKey: authKeys.all,
      });
      setIsUpdatingAvailability(false);
    },
    onError: () => {
      setIsUpdatingAvailability(false);
    },
  });

  const handleAvailabilityToggle = (checked: boolean) => {
    setIsUpdatingAvailability(true);
    updateAvailabilityMutation.mutate(checked);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with user info */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t('home.welcomeUser', { name: user.name })}
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                {user.isOrganizer ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    {t('home.organizer')}
                  </span>
                ) : (
                  <span>{t('home.member')}</span>
                )}
              </p>
            </div>
            <button
              onClick={logout}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              {t('auth.logout')}
            </button>
          </div>

          {/* Default Availability Switch */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={user.defaultAvailability || false}
                onChange={(e) => handleAvailabilityToggle(e.target.checked)}
                disabled={isUpdatingAvailability}
                className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 transition-opacity"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">
                  {t('home.defaultAvailable', 'Default available')}
                </span>
                <p className="text-xs text-gray-500">
                  {t('home.defaultAvailableDesc', 'Mark yourself available by default for new weeks')}
                </p>
              </div>
            </label>
            {isUpdatingAvailability && (
              <svg
                className="animate-spin h-4 w-4 text-primary-600 ml-auto"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
          </div>
        </div>

        {/* Main content */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {t('home.quickAccess')}
          </h2>

          {/* Navigation Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {/* Weekly Menu Card */}
            <Link
              to="/menu"
              className="group bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              aria-label="View weekly menu"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-3 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors">
                  <svg
                    className="h-6 w-6 text-primary-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-primary-700 transition-colors">
                    {t('home.weeklyMenu')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('home.weeklyMenuDescription')}
                  </p>
                </div>
              </div>
            </Link>

            {/* Availability Card */}
            <Link
              to="/availability"
              search={{ tab: 'personal' }}
              className="group bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              aria-label="Manage your availability"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-3 bg-secondary-100 rounded-lg group-hover:bg-secondary-200 transition-colors">
                  <svg
                    className="h-6 w-6 text-secondary-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-secondary-700 transition-colors">
                    {t('home.myAvailability')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('home.myAvailabilityDescription')}
                  </p>
                </div>
              </div>
            </Link>

            {/* Weekly Selection Card */}
            <Link
              to="/weekly-selection"
              search={{ tab: 'personal' }}
              className="group bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              aria-label="Pick your meals for this week"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                  <svg
                    className="h-6 w-6 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-purple-700 transition-colors">
                    {t('home.pickWeekMeals')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('home.pickWeekMealsDescription')}
                  </p>
                </div>
              </div>
            </Link>

            {/* Browse Meals Card */}
            <Link
              to="/meals"
              className="group bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              aria-label="Browse meals"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-3 bg-success-100 rounded-lg group-hover:bg-success-200 transition-colors">
                  <svg
                    className="h-6 w-6 text-success-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-success-700 transition-colors">
                    {t('home.browseMeals')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('home.browseMealsDescription')}
                  </p>
                </div>
              </div>
            </Link>

            {/* Profile Card */}
            <Link
              to="/profile"
              className="group bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              aria-label="View your profile"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-3 bg-info-100 rounded-lg group-hover:bg-info-200 transition-colors">
                  <svg
                    className="h-6 w-6 text-info-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-info-700 transition-colors">
                    {t('home.myProfile')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('home.myProfileDescription')}
                  </p>
                </div>
              </div>
            </Link>

            {/* Products Card - Organizer Only */}
            {user.isOrganizer && (
              <Link
                to="/products"
                className="group bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                aria-label="Manage products"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 p-3 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors">
                    <svg
                      className="h-6 w-6 text-orange-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-orange-700 transition-colors">
                      {t('products.title')}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t('products.description')}
                    </p>
                  </div>
                </div>
              </Link>
            )}

            {/* Recipes Card - Organizer Only */}
            {user.isOrganizer && (
              <Link
                to="/recipes"
                className="group bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                aria-label="Manage recipes"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 p-3 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                    <svg
                      className="h-6 w-6 text-emerald-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-emerald-700 transition-colors">
                      {t('recipes.title')}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t('recipes.description')}
                    </p>
                  </div>
                </div>
              </Link>
            )}

            {/* Create Meal Card - Organizer Only */}
            {user.isOrganizer && (
              <Link
                to="/meals/new"
                className="group bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                aria-label="Create a new meal"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 p-3 bg-success-100 rounded-lg group-hover:bg-success-200 transition-colors">
                    <svg
                      className="h-6 w-6 text-success-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-success-700 transition-colors">
                      {t('home.createMeal')}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t('home.createMealDescription')}
                    </p>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
